import editorialProducts from '../data/iceGearCatalog.json';
import productImages from '../config/gearProductImages.json';
import manufacturerProductInfo from '../data/manufacturerProductInfo.json';
import { CSV_LOOKUP_TO_URL_MAPPING } from '../utils/productUrlMapping';
import { isCurrentVerifiedPurchasableOffer, OFFER_CURRENT_WINDOW_MS, selectCurrentVerifiedBest, sortCustomerOffers } from './offerFreshness';
import { getOfferSavings } from './offerSavings';
import { getOfferLink } from '../components/PriceComparison/simpleProductPageShared';

export const ICE_SEASON = '2026-27';
export const GEAR_PRODUCTS = editorialProducts;
export const formatGearPrice = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
export const gearPath = (product) => product.detailPath || `/gear/${product.slug}`;
function informationPrice(info) {
  const checkedAt=Date.parse(info?.checkedAt);
  if(!Number.isFinite(checkedAt)||Date.now()-checkedAt>OFFER_CURRENT_WINDOW_MS) return null;
  if(info?.price>0) return info.price;
  if(info?.priceRange?.min>0 && info.priceRange.max>=info.priceRange.min) return info.priceRange.min;
  return null;
}
export function formatInformationPrice(info) {
  if(informationPrice(info)==null) return null;
  if(info?.price>0) return formatGearPrice(info.price);
  if(info?.priceRange?.min>0 && info.priceRange.max>=info.priceRange.min)
    return `${formatGearPrice(info.priceRange.min)}–${formatGearPrice(info.priceRange.max)}`;
  return null;
}
const productAliases={'echomap-uhd2-73sv':'echomap-73sv-uhd2','garmin-lvs62':'livescope-xr-lvs62'};
export function resolveGearProduct(products, identities=[], pathname='') {
  for(const identity of identities.flat().filter(Boolean)){const canonical=productAliases[identity]||identity;const product=products.find(p=>p.slug===canonical||p.productId===canonical);if(product)return product;}
  const normalize=value=>{try{return decodeURIComponent(value).replace(/\/+$/,'');}catch{return value;}};
  return products.find(p=>normalize(gearPath(p))===normalize(pathname));
}
export const releaseLabel = (product) => ({ new: 'New for 2026–27', updated: 'Updated for 2026–27', existing: 'Established gear', watchlist: 'Release watch' }[product.releaseState] || 'Established gear');

export function releaseBrowseLink(product) {
  const params = new URLSearchParams();
  if (product.releaseState === 'watchlist') params.set('season','watchlist');
  else {
    params.set('release',product.releaseState || 'existing');
    if (product.season && ['new','updated'].includes(product.releaseState)) params.set('season',product.season);
  }
  return `/price-comparison?${params}`;
}

const categories = { screens: 'Fish Finders', transducers: 'Sonar & Transducers', batteries: 'Batteries & Chargers', shuttles: 'Shuttles & Cases', poles: 'Poles & Mounts', packages: 'Electronics Bundles', sonar_module: 'Sonar & Transducers' };
const hiddenDuplicateSlugs = new Set(Object.keys(productAliases));

function hasRetailerDestination(retailer, url) {
  if (!retailer || /geniuslink|more retailers/i.test(retailer)) return false;
  try { return new URL(url).protocol === 'https:'; } catch { return false; }
}

const isDiscounted = product => product.bestPrice != null && product.savings?.amount > 0;
const catalogPrice = product => product.bestPrice ?? informationPrice(product.informationFallback);

export const getGearStandardPrice = (product, offer) => product.standardPriceByOffer?.[offer?.product_offer_id]?.price
  ?? (product.msrpRange ? null : product.msrp);

export function buildGearCatalog(products = [], offers = [], now = Date.now()) {
  const databaseProducts=new Map(products.map(p=>[p.id,p]));
  const byProduct = new Map();
  offers.forEach((offer) => {
    const list = byProduct.get(offer.product_id) || [];
    list.push({ ...offer, Source: offer.website, 'Job Link': offer.affiliate_url, numeric_price: Number(offer.current_price) || null });
    byProduct.set(offer.product_id, list);
  });
  const editorialBySlug = new Map(GEAR_PRODUCTS.map(p => [p.slug, p]));
  const editorialDbIds = new Set(GEAR_PRODUCTS.flatMap(p => p.productId ? [p.productId] : []));
  const existing = products.filter(p => p.is_active && !editorialDbIds.has(p.id) && !editorialBySlug.has(p.slug) && !hiddenDuplicateSlugs.has(p.slug)).map(p => ({
    id: p.id, productId: p.id, slug: p.slug, name: p.name, brand: p.brand || 'Other',
    category: categories[p.category] || 'Fishing Gear',
    activities: ['screens', 'transducers', 'sonar_module'].includes(p.category) && !/ice|\bif\b/i.test(p.name) ? ['ice', 'open-water'] : /open.water|all.terrain|deck.mount|track.mount/i.test(p.name) ? ['open-water'] : ['ice'],
    releaseState: 'existing', image: p.image_url, msrp: Number(p.msrp) || null, msrpLabel: 'MSRP',
    detailPath: CSV_LOOKUP_TO_URL_MAPPING[p.name] || `/simple-products/db/${encodeURIComponent(p.slug)}`,
    retailerLinks: [], specs: [],
  }));
  return [...GEAR_PRODUCTS, ...existing].map(product => {
    const candidateImage = productImages[product.productId];
    const reviewedImage = candidateImage?.slug === product.slug ? candidateImage : null;
    const rows = (byProduct.get(product.productId) || []).filter(row=>row.is_active!==false && hasRetailerDestination(row.Source,getOfferLink(row)));
    const currentOffers = sortCustomerOffers(rows.filter(o => isCurrentVerifiedPurchasableOffer(o, now)), { now });
    const displayOffers = currentOffers;
    const best = selectCurrentVerifiedBest(currentOffers, { now });
    // A known URL is not a verified buying option. Unquoted, unavailable and stale
    // destinations must never reappear as "Check price" rows.
    const retailerLinks = currentOffers.map(row => ({ retailer: row.Source, url: getOfferLink(row) }));
    const reference = getGearStandardPrice(product, best.primary);
    return { ...product, image: product.image || reviewedImage?.src, imageFallback: reviewedImage?.originalImage,
      imageCaption: reviewedImage?.caption, imageKind: reviewedImage?.kind || 'product-photo',
      msrp: reference, retailerLinks, currentOffers, displayOffers, bestPrice: best.lowestPrice, bestOffer: best.primary,
      lowestRetailers: best.tied.map(o => o.Source), savings: getOfferSavings(best.primary, reference, product.msrpLabel || 'Standard price'),
      hasBuyingOptions: retailerLinks.length > 0,
      informationFallback: retailerLinks.length ? null : databaseProducts.get(product.productId)?.manufacturer_info || manufacturerProductInfo[product.slug] || null,
      retailerCount: new Set([...displayOffers.map(o => o.Source), ...retailerLinks.map(l => l.retailer)]).size };
  });
}

export function filterGearCatalog(products, { q = '', brand = '', category = '', activity = '', season = '', release = '', sort = 'featured', priced = '', sale = '', includeWatchlist = false } = {}) {
  const terms = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
  return products.filter(p => terms.every(t => `${p.brand} ${p.name} ${p.category} ${(p.searchTerms || '')}`.toLowerCase().includes(t))
    && (includeWatchlist || season === 'watchlist' || p.releaseState !== 'watchlist')
    && (!brand || p.brand === brand) && (!category || p.category === category)
    && (!release || p.releaseState === release)
    && (!activity || p.activities?.includes(activity))
    && (!season || (season === 'watchlist' ? p.releaseState === 'watchlist' : p.season === season && ['new','updated'].includes(p.releaseState)))
    && (!priced || catalogPrice(p) != null)
    && (!sale || isDiscounted(p)))
    .sort((a,b) => {
      if (sort === 'price-low' || sort === 'price-high') {
        const aPrice=catalogPrice(a),bPrice=catalogPrice(b);
        if (aPrice == null || bPrice == null) return (aPrice == null ? 1 : 0) - (bPrice == null ? 1 : 0) || a.name.localeCompare(b.name);
        return (aPrice-bPrice)*(sort === 'price-high' ? -1 : 1) || a.name.localeCompare(b.name);
      }
      if (sort === 'brand') return a.brand.localeCompare(b.brand) || a.name.localeCompare(b.name);
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'deals') {
        const discountOrder = Number(isDiscounted(b)) - Number(isDiscounted(a));
        if (discountOrder) return discountOrder;
        const featuredOrder = (a.featured ?? 999) - (b.featured ?? 999);
        if (featuredOrder) return featuredOrder;
        if (isDiscounted(a)) {
          const savingsOrder = (b.savings.percent - a.savings.percent) || (b.savings.amount - a.savings.amount);
          if (savingsOrder) return savingsOrder;
        }
      }
      return (a.featured || 999)-(b.featured || 999) || a.brand.localeCompare(b.brand) || a.name.localeCompare(b.name);
    });
}
